// Getting a tablet onto a stand: the code a crew member types into it.
//
// ---------------------------------------------------------------------------
// WHY THIS SCREEN EXISTS AT ALL
//
// `POST /v1/admin/enrolment-codes` has existed since the identity surface was
// built and nothing has ever called it but curl. A tablet cannot join an
// activation without a code, so until this screen there was no way to set a
// stand up that did not involve somebody pasting XP_ADMIN_TOKEN into a terminal
// — which means the credential that can reconfigure every tablet in the estate
// was being handled routinely, by whoever happened to be setting up, for the
// most ordinary operation there is. That is the opposite of what the token was
// protecting, and it is the argument that moved the route's authentication. The
// service's half is api/src/auth/freshFactor.ts.
//
// ---------------------------------------------------------------------------
// THE CODE IS SHOWN ONCE AND THIS SCREEN SAYS SO BEFORE IT IS ISSUED
//
// identity/routes.ts hashes it on the way into the database — "a code the server
// could read back later is a code a dump of the database yields" — so the
// response is the only place it will ever exist in plain. A screen that showed
// it without saying that invites somebody to close the tab and come back for it.
//
// It is held in React state and nowhere else, on Enrol.tsx's reasoning about the
// enrolment secret. There is no route that reads it back, so a copy that
// outlived the page could never be checked against anything.
//
// ---------------------------------------------------------------------------
// NO RULE ABOUT STATIONS, EXPIRY OR WHO MAY ISSUE ONE IS WRITTEN HERE
//
// The lifetime is ENROLMENT.codeLifetimeSeconds on the frozen contract and the
// expiry comes back on the response, so this draws the service's instant rather
// than adding a number to a clock. Whether a station identifier is required is
// the schema's decision — it takes `v.nullable`, so absent and null are
// different requests — and the field offers both without judging either.
//
// ---------------------------------------------------------------------------
// THE DESIGN'S PAGE (TASKS §143, step 5)
//
// Its head, its three fields with their help, and "Create a code". The Kiosk
// pages are the rail's, so the copy of them this page drew over its own form
// is gone. The authenticator field is Authorise.tsx, which the design draws as
// a plain six-digit field: it is the same field with the console's handling
// of a spent code. The button stays pressable with a field empty, because the
// service's refusal says which one and a greyed button does not.

import { useState, type ReactNode } from "react";
import { FieldRow } from "@nova/desk-ui/kit";
import { NoticeBox } from "@nova/desk-ui/parts";
import { Loading } from "@nova/desk-ui/states";
import type { ActivationLine } from "../../../operator/src/activations.js";
import type { ApiOptions } from "../api/client.js";
import { issueEnrolmentCode, type AdminRefusal, type EnrolmentCode } from "../api/admin.js";
import { AdminRefused, Authorise } from "./Authorise.js";
import { ReadFailed } from "./ReadFailed.js";
import { useShow } from "./Show.js";
import type { SessionToken } from "../session/store.js";

export type TabletsProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly activationId: string;
  readonly onSessionEnded: (detail: string) => void;
  readonly role: string;
  readonly onEnrol: () => void;
};

type Issuing =
  | { readonly phase: "idle" }
  | { readonly phase: "sending" }
  | { readonly phase: "issued"; readonly code: EnrolmentCode }
  | { readonly phase: "refused"; readonly refusal: AdminRefusal };

export function Tablets({
  api,
  token,
  activationId,
  onSessionEnded,
  role,
  onEnrol,
}: TabletsProps): ReactNode {
  const show = useShow();
  const [station, setStation] = useState("");
  const [issuedBy, setIssuedBy] = useState("");
  const [code, setCode] = useState("");
  const [issuing, setIssuing] = useState<Issuing>({ phase: "idle" });

  const activation: ActivationLine | null = show.status === "ready" ? show.line : null;

  const head = (
    <header className="head">
      <h1>Add a tablet</h1>
      <p className="lede">Create a one-time code, then enter it on the tablet.</p>
    </header>
  );

  if (show.status === "idle" || show.status === "reading") {
    return (
      <div className="stack">
        {head}
        <Loading rows={3} cols={1} label="Reading the activation…" />
      </div>
    );
  }

  if (show.status === "failed") {
    return (
      <div className="stack">
        {head}
        <ReadFailed
          what="Could not load the activation"
          failure={show.failure}
          role={role}
          onEnrol={onEnrol}
          notCounted="No code was issued and no tablet was changed."
          onRetry={show.reload}
        />
      </div>
    );
  }

  if (activation === null) {
    // The same distinction Kiosk.tsx draws: in the list the server sent and not
    // in it are different answers, and this is the second. The console must not
    // guess whether that is a mistyped url or an ungranted activation, because
    // 004 decides the second one in the database.
    return (
      <div className="stack">
        <header className="head">
          <h1>Activation not found</h1>
          <p className="lede">Check the link or ask Nova to grant this account access.</p>
        </header>
        <p className="mono quiet">{activationId}</p>
      </div>
    );
  }

  const busy = issuing.phase === "sending";

  const issue = async (): Promise<void> => {
    setIssuing({ phase: "sending" });
    const result = await issueEnrolmentCode(api, token, {
      activationId,
      // Blank is null, which is the attendee-device surface rather than a
      // station nobody named. The schema takes `v.nullable` rather than
      // `v.optional` precisely so the two are distinguishable, and trimming to
      // null here is what keeps a stray space from becoming a station id.
      stationId: station.trim() === "" ? null : station.trim(),
      issuedBy,
      secondFactorCode: code,
    });

    // Cleared on both outcomes, per Authorise.tsx: the step is spent either way,
    // so a field still holding it would offer somebody digits that cannot work.
    setCode("");

    if (result.ok) {
      setIssuing({ phase: "issued", code: result.value });
      return;
    }

    setIssuing({ phase: "refused", refusal: result });
    if (result.kind === "session_ended") onSessionEnded(result.detail);
  };

  return (
    <div className="stack">
      {head}

      <section>
        <h2 className="sec">Issue a code</h2>

        <div className="panel panel-stack">
          <FieldRow
            label="Station name (optional)"
            help="Leave blank for a tablet used directly by attendees. Named tablets appear by this name in reports."
          >
            <input
              className="input"
              value={station}
              onChange={(event) => setStation(event.target.value)}
              disabled={busy}
            />
          </FieldRow>

          <FieldRow label="Issued by" help="Use a name the crew will recognise. It is stored with the code.">
            <input
              className="input"
              value={issuedBy}
              onChange={(event) => setIssuedBy(event.target.value)}
              disabled={busy}
            />
          </FieldRow>

          <Authorise
            value={code}
            onChange={setCode}
            busy={busy}
            what="issue this code"
            onEnter={() => {
              if (!busy) void issue();
            }}
          />

          <div className="acts acts-flush">
            <button type="button" className="btn" disabled={busy} onClick={() => void issue()}>
              {busy ? "Creating…" : "Create a code"}
            </button>
          </div>
        </div>
      </section>

      {issuing.phase === "refused" ? <AdminRefused refusal={issuing.refusal} /> : null}

      {issuing.phase === "issued" ? <Issued code={issuing.code} /> : null}
    </div>
  );
}

/**
 * The code, once, with the two facts that make it usable.
 *
 * THE EXPIRY IS THE SERVICE'S INSTANT AND IS NOT REFORMATTED. Choose.tsx's
 * header argues this for activation dates — rendering an instant in the reader's
 * zone moves a stand that ran in Dubai to the day before — and it bites harder on
 * a value with a ten-minute life: a code shown as expiring at a time in the wrong
 * zone is a code somebody stops trying an hour before it dies, or keeps trying an
 * hour after.
 */
function Issued({ code }: { code: EnrolmentCode }): ReactNode {
  return (
    <NoticeBox kind="good" title="Type this into the tablet">
      {/* The code itself, large and selectable, which until now it was neither.
          It borrowed the enrolment screen's secret-input modifier, and that rule
          is written for a readonly text box: tracking, tabular figures and a
          full width. On a paragraph it set the tracking and nothing else — no
          size, no user-select — so the note that used to stand here was
          describing a rule that says neither of the two things it claimed. The
          design has a class for exactly this value and .secret is it, on this
          same screen in handoff/console-kiosk.jsx. */}
      <p className="secret">{code.code}</p>

      <p>
        Use it once before <strong>{code.expiresAt}</strong>.
      </p>
      <p>
        {code.stationId === null
          ? "This code adds an attendee-facing tablet."
          : `This code adds the tablet as ${code.stationId}.`}
      </p>
    </NoticeBox>
  );
}
